import type { CSSProperties, MouseEvent } from 'react'
import { forwardRef, memo, useEffect, useRef, useState } from 'react'
import { TableCell as UITableCell } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { flexRender } from '@tanstack/react-table'

import { isFreeTextColumn } from '../free-text-columns'
import { ColumnMeta, TableCellProps } from '../types'

const compareComparableValue = (
  prevValue: unknown,
  nextValue: unknown,
): boolean => {
  if (prevValue === nextValue) return true
  if (typeof prevValue !== typeof nextValue) return false

  if (prevValue == null || nextValue == null) return prevValue === nextValue

  if (typeof prevValue === 'object') {
    if (typeof nextValue !== 'object') return false
    return JSON.stringify(prevValue) === JSON.stringify(nextValue)
  }

  return false
}

export const TableCell = memo(
  forwardRef<HTMLTableCellElement, TableCellProps>(
    (
      {
        cell,
        isRowLoading,
        isDraggable,
        isDraggingTable,
        isScrolling,
        isActivelyResizing,
        columnWidth,
        rowId,
        isSelected,
        isActiveSelection,
        isFillPreview,
        showFillHandle,
        enableCellSelection,
        item,
        disableCellSelection,
        onCellClick,
        onCellDoubleClick,
        onCellSelect,
        onFillHandlePointerDown,
      },
      ref,
    ) => {
      const { column } = cell
      const meta = column.columnDef.meta as ColumnMeta | undefined
      const isFirstColumn = cell.column.getIndex() === 0
      const isViewColumn = column.id === 'view'
      const itemType =
        item && typeof item === 'object' && 'type' in item
          ? (item as { type?: unknown }).type
          : undefined
      const shouldShowClickableTextDecoration = itemType !== 'add-task'
      // Free text keeps its ellipsis when it runs out of room; every other
      // cell clips (see free-text-columns.ts and table-styles.css).
      const isFreeText = isFreeTextColumn(
        column.id,
        meta?.fieldType,
        meta?.customFieldDefinitionId,
      )

      // Apply column width from resizing hook if available
      const finalWidth = columnWidth
        ? `${columnWidth}px`
        : meta?.width || 'auto'

      const canSelect = Boolean(
        enableCellSelection && rowId && !disableCellSelection,
      )
      const isInteractiveTarget = (target: HTMLElement): boolean =>
        Boolean(
          target.closest(
            'button, [role="button"], [role="option"], input, textarea, select, [contenteditable="true"], [data-editable-cell="true"]',
          ),
        )

      const handleCellPointerDownCapture = (e: MouseEvent) => {
        if (!canSelect || !onCellSelect) return
        if (e.button !== 0) return

        // Don't capture events from popover/dropdown triggers to allow them to open properly
        const target = e.target as HTMLElement
        if (isInteractiveTarget(target)) {
          return
        }
        if (
          target.closest(
            '[data-radix-collection-item], [data-state], [role="combobox"], [data-edit-trigger]',
          )
        ) {
          return
        }

        e.preventDefault()
        window.getSelection()?.removeAllRanges()
        onCellSelect(
          String(rowId),
          column.id,
          e.currentTarget as HTMLTableCellElement,
          {
            extend: e.shiftKey,
            startDrag: true,
          },
        )
      }

      // Handle cell click
      const handleCellClick = (e: MouseEvent) => {
        // Don't trigger cell click if we're in edit mode or clicking on interactive elements
        const target = e.target as HTMLElement

        // Check if we're clicking on an interactive element
        if (
          target.closest(
            'button, [role="button"], [role="option"], input, select, textarea, [data-task-name-input]',
          )
        ) {
          return
        }

        // Check if we're clicking on a field that's currently being edited
        if (target.closest('[data-editing="true"]')) {
          if (canSelect && onCellSelect) {
            onCellSelect(
              String(rowId),
              column.id,
              e.currentTarget as HTMLTableCellElement,
              {
                extend: e.shiftKey,
              },
            )
          }
          return
        }

        // Check if there's text selection (user is selecting text)
        if (window.getSelection()?.toString()) {
          return
        }

        if (canSelect && onCellSelect) {
          onCellSelect(
            String(rowId),
            column.id,
            e.currentTarget as HTMLTableCellElement,
            {
              extend: e.shiftKey,
            },
          )
        }

        // Call the cell click handler if provided
        if (onCellClick && item) {
          onCellClick(item, e)
        }
      }

      // Handle cell double click
      const handleCellDoubleClick = (e: MouseEvent) => {
        // Don't trigger cell double click on interactive elements
        const target = e.target as HTMLElement

        if (
          target.closest(
            'button, [role="button"], input, select, textarea, [data-task-name-input]',
          )
        ) {
          return
        }

        // Call the cell double click handler if provided
        if (onCellDoubleClick && item) {
          onCellDoubleClick(item, e)
        }
      }

      // Determine if this cell should be clickable
      const isClickable = !!(onCellClick || onCellDoubleClick)
      const isInteractive = isClickable || canSelect

      // Gentle flash on value change (real-time updates)
      const getComparableValue = (
        val: unknown,
      ): string | number | boolean | null => {
        if (val == null) return null
        if (
          typeof val === 'string' ||
          typeof val === 'number' ||
          typeof val === 'boolean'
        )
          return val
        try {
          return JSON.stringify(val)
        } catch {
          return String(val)
        }
      }
      const currentRaw = (() => {
        try {
          // Prefer accessor value when available. Safely access optional getValue.
          const getter = (cell as unknown as { getValue?: () => unknown })
            .getValue
          const raw = typeof getter === 'function' ? getter() : undefined
          return raw !== undefined ? raw : cell.renderValue?.()
        } catch {
          return undefined
        }
      })()
      const currentComparable = getComparableValue(currentRaw)
      const prevComparableRef =
        useRef<typeof currentComparable>(currentComparable)
      const mountedRef = useRef(false)
      const [flash, setFlash] = useState(false)

      useEffect(() => {
        if (!mountedRef.current) {
          mountedRef.current = true
          prevComparableRef.current = currentComparable
          return
        }
        if (currentComparable !== prevComparableRef.current) {
          setFlash(true)
          const t = setTimeout(() => setFlash(false), 900)
          prevComparableRef.current = currentComparable
          return () => clearTimeout(t)
        }
      }, [currentComparable])

      return (
        <UITableCell
          ref={ref}
          className={cn(
            meta?.className,
            'relative',
            isRowLoading && 'bg-background',
            isDraggable && 'bg-background',
            isDraggingTable && 'bg-background',
            isScrolling && 'bg-background',
            isActivelyResizing && 'bg-background',
            'focus:outline-none focus-visible:outline-none',
            // Add clickable styling
            isInteractive && 'group/cell',
            isClickable && 'cursor-pointer',
            canSelect && 'cursor-cell',
            // Add hover effects for clickable cells
            isInteractive && 'transition-colors duration-150 hover:bg-muted/30',
            // First column clickable indicator - text becomes primary color and underlined on hover with pointer cursor
            isFirstColumn &&
              isClickable &&
              shouldShowClickableTextDecoration &&
              'cursor-pointer hover:text-primary hover:underline',
            isSelected && 'bg-primary/5 ring-1 ring-inset ring-primary/30',
            isActiveSelection &&
              'bg-primary/10 ring-2 ring-inset ring-primary/50',
            isFillPreview && 'bg-primary/10 ring-1 ring-inset ring-primary/40',
          )}
          style={
            {
              // CSS custom property for responsive design
              '--column-width': finalWidth,
            } as CSSProperties
          }
          data-column={column.id}
          data-free-text={isFreeText || undefined}
          data-fixed={isFirstColumn || isViewColumn}
          data-clickable={isClickable}
          data-selected={isSelected || undefined}
          data-active-selection={isActiveSelection || undefined}
          tabIndex={-1}
          onMouseDownCapture={
            isInteractive ? handleCellPointerDownCapture : undefined
          }
          onClick={isInteractive ? handleCellClick : undefined}
          onDoubleClick={isClickable ? handleCellDoubleClick : undefined}
        >
          {flash && (
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-0 rounded-sm',
                'ring-2 ring-emerald-400/40',
                'bg-emerald-400/10',
                'animate-pulse',
              )}
              style={{ animationDuration: '900ms' }}
            />
          )}
          {showFillHandle && (
            <button
              type="button"
              aria-label="Fill selected cells down"
              className="absolute -bottom-1 -right-1 z-20 h-2.5 w-2.5 cursor-crosshair rounded-full border border-background bg-primary shadow-sm"
              data-fill-handle="true"
              onPointerDown={onFillHandlePointerDown}
            />
          )}
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </UITableCell>
      )
    },
  ),
  (prev, next) => {
    if (prev.cell.id !== next.cell.id) return false

    if (prev.rowId !== next.rowId) return false
    if (prev.isRowLoading !== next.isRowLoading) return false
    if (prev.isDraggable !== next.isDraggable) return false
    if (prev.isDraggingTable !== next.isDraggingTable) return false
    if (prev.isScrolling !== next.isScrolling) return false
    if (prev.isActivelyResizing !== next.isActivelyResizing) return false
    if (prev.columnWidth !== next.columnWidth) return false
    if (prev.isSelected !== next.isSelected) return false
    if (prev.isActiveSelection !== next.isActiveSelection) return false
    if (prev.isFillPreview !== next.isFillPreview) return false
    if (prev.showFillHandle !== next.showFillHandle) return false
    if (prev.enableCellSelection !== next.enableCellSelection) return false
    if (prev.disableCellSelection !== next.disableCellSelection) return false
    if (prev.item !== next.item) return false
    if (prev.onCellClick !== next.onCellClick) return false
    if (prev.onCellDoubleClick !== next.onCellDoubleClick) return false
    if (prev.onCellSelect !== next.onCellSelect) return false
    if (prev.onFillHandlePointerDown !== next.onFillHandlePointerDown)
      return false

    const getCellComparable = (tableCell: {
      getValue?: () => unknown
      renderValue?: () => unknown
    }) => {
      try {
        const getter = tableCell?.getValue
        const raw = typeof getter === 'function' ? getter() : undefined
        return raw !== undefined ? raw : tableCell.renderValue?.()
      } catch {
        return undefined
      }
    }

    const prevValue = getCellComparable(prev.cell)
    const nextValue = getCellComparable(next.cell)

    return compareComparableValue(prevValue, nextValue)
  },
)

TableCell.displayName = 'DataTable.TableCell'
